import { Clipboard } from '@angular/cdk/clipboard'
import { BreakpointObserver } from '@angular/cdk/layout'
import { NgClass, NgTemplateOutlet } from '@angular/common'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererFactory2,
  signal,
  viewChild
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { Webhook, WebhookLog } from '@app/v2/settings/enums/webhooks.model'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { WebhookService } from '@app/v2/settings/services/webhook.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { finalize, map } from 'rxjs'

type MobileView = 'list' | 'preview'

type WebhookLogListItem = {
  delivery: WebhookLog
  trigger: string
  webhookUrl: string
  deliveredAt: string
  isSuccess: boolean
}

type WebhookLogPreview = {
  delivery: WebhookLog
  trigger: string
  deliveredAt: string
  formattedRequestBody: string
  formattedResponseRaw: string
  isSuccess: boolean
}

@Component({
  selector: 'webhook-logs',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, TranslateModule, NgxSkeletonLoaderModule, NgxTippyModule],
  templateUrl: './webhook-logs.component.html',
  styleUrls: ['./webhook-logs.component.scss'],
  providers: [WebhookService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebhookLogsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public webhook!: Webhook

  public readonly deliveries = signal<WebhookLog[]>([])
  public readonly deliveryListItems = signal<WebhookLogListItem[]>([])
  public readonly selectedDeliveryId = signal<number | null>(null)
  public readonly isLoading = signal(false)
  public readonly currentPage = signal(1)
  public readonly lastPage = signal(1)
  public readonly mobileView = signal<MobileView>('list')

  public readonly isMobileLayout = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 1023px)')
      .pipe(map((result) => result.matches)),
    {
      initialValue: typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
    }
  )

  public readonly selectedDelivery = computed(() => {
    const id = this.selectedDeliveryId()
    return this.deliveries().find((delivery) => delivery.id === id) ?? null
  })

  public readonly selectedDeliveryPreview = computed<WebhookLogPreview | null>(() => {
    const delivery = this.selectedDelivery()
    if (!delivery) return null

    return {
      delivery,
      trigger: this._getTrigger(delivery),
      deliveredAt: this._formatDeliveredAt(delivery.created_at),
      formattedRequestBody: this._formatJson(delivery.request?.body),
      formattedResponseRaw: this._formatJson(delivery.response?.raw),
      isSuccess: this._isSuccess(delivery)
    }
  })

  public readonly pageNumbers = computed(() => this._buildPageNumbers(this.currentPage(), this.lastPage()))

  public readonly logsTitle = computed(() => `${this.webhook.name} / ${this.webhook.event_slug}`)

  public readonly retriesTippyProps = {
    theme: 'dark' as const,
    placement: 'top' as const,
    appendTo: () => document.body,
    zIndex: 200_000
  }

  private readonly _dateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short'
  })

  private readonly _timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _injector = inject(Injector)
  private readonly _renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null)
  private readonly _webhookService = inject(WebhookService)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _clipboard = inject(Clipboard)
  private readonly _alertService = inject(AlertService)
  private readonly _translate = inject(TranslateService)

  private readonly _mobilePreviewOverlay = viewChild<ElementRef<HTMLElement>>('mobilePreviewOverlay')

  private _mobilePreviewOriginalParent: HTMLElement | null = null

  private readonly _perPage = 8

  ngOnInit(): void {
    this._loadDeliveries(1)
  }

  ngOnDestroy(): void {
    this._restoreMobilePreviewOverlay()
  }

  public selectDelivery(delivery: WebhookLog): void {
    this.selectedDeliveryId.set(delivery.id)
  }

  public openMobilePreview(delivery: WebhookLog, event: Event): void {
    event.stopPropagation()
    this.selectedDeliveryId.set(delivery.id)
    this.mobileView.set('preview')

    afterNextRender(
      () => {
        this._portalMobilePreviewOverlay()
      },
      { injector: this._injector }
    )
  }

  public closeMobilePreview(): void {
    this._restoreMobilePreviewOverlay()
    this.mobileView.set('list')
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.lastPage() || page === this.currentPage() || this.isLoading()) return
    this._loadDeliveries(page)
  }

  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  public copyToClipboard(value: Record<string, unknown> | string | null | undefined): void {
    const text = this._formatJson(value)
    if (!text) return

    this._clipboard.copy(text)
    this._alertService.alert(Alert.SUCCESS, this._translate.instant('v2.orgSettings.webhooks.copiedToClipboard'))
  }

  private _loadDeliveries(page: number): void {
    this.isLoading.set(true)
    this._webhookService
      .getWebhookLogs(this.webhook.id, { page, per_page: this._perPage })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (result) => {
          this.deliveries.set(result.items)
          this.deliveryListItems.set(result.items.map((item) => this._toListItem(item)))
          this.currentPage.set(result.current_page)
          this.lastPage.set(result.last_page)
          this._restoreMobilePreviewOverlay()
          this.mobileView.set('list')
          this._syncSelectedDelivery(result.items)
        },
        error: (error) => {
          console.error('Error loading webhook deliveries:', error)
          this.deliveries.set([])
          this.deliveryListItems.set([])
          this.selectedDeliveryId.set(null)
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.webhooks.loadWebhookLogsError'))
        }
      })
  }

  private _toListItem(delivery: WebhookLog): WebhookLogListItem {
    return {
      delivery,
      trigger: this._getTrigger(delivery),
      webhookUrl: this._getWebhookUrl(delivery),
      deliveredAt: this._formatDeliveredAt(delivery.created_at),
      isSuccess: this._isSuccess(delivery)
    }
  }

  private _formatDeliveredAt(value: string): string {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return `${this._dateFormatter.format(date)}, ${this._timeFormatter.format(date)}`
  }

  private _formatJson(value: Record<string, unknown> | string | null | undefined): string {
    if (value == null) return ''
    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch {
        return value
      }
    }

    return JSON.stringify(value, null, 2)
  }

  private _getTrigger(log: WebhookLog): string {
    const meta = log.request?.body?.meta as Record<string, unknown> | undefined
    return String(meta?.trigger ?? '')
  }

  private _getWebhookUrl(log: WebhookLog): string {
    const meta = log.request?.body?.meta as Record<string, unknown> | undefined
    return String(meta?.webhook_url ?? '')
  }

  private _isSuccess(log: WebhookLog): boolean {
    return log.response_status >= 200 && log.response_status < 300
  }

  private _syncSelectedDelivery(items: WebhookLog[]): void {
    const selectedId = this.selectedDeliveryId()
    if (selectedId != null && items.some((item) => item.id === selectedId)) return

    this.selectedDeliveryId.set(items[0]?.id ?? null)
  }

  private _buildPageNumbers(current: number, last: number): Array<number | 'ellipsis'> {
    if (last <= 1) return [1]
    if (last <= 5) return Array.from({ length: last }, (_, index) => index + 1)

    const pages = new Set<number>([1, last, current])
    if (current > 1) pages.add(current - 1)
    if (current < last) pages.add(current + 1)

    const sorted = Array.from(pages).sort((a, b) => a - b)
    const result: Array<number | 'ellipsis'> = []

    sorted.forEach((page, index) => {
      const previous = sorted[index - 1]
      if (previous != null && page - previous > 1) result.push('ellipsis')
      result.push(page)
    })

    return result
  }

  private _portalMobilePreviewOverlay(): void {
    const overlay = this._mobilePreviewOverlay()?.nativeElement
    if (!overlay || overlay.parentElement === document.body) return

    this._mobilePreviewOriginalParent ??= overlay.parentElement
    this._renderer.appendChild(document.body, overlay)
  }

  private _restoreMobilePreviewOverlay(): void {
    const overlay = this._mobilePreviewOverlay()?.nativeElement
    if (!overlay || !this._mobilePreviewOriginalParent || overlay.parentElement !== document.body) return

    this._renderer.appendChild(this._mobilePreviewOriginalParent, overlay)
  }
}
